import isNil from 'lodash/isNil';
import repeat from 'lodash/repeat';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import omitBy from 'lodash/omitBy';
import { v4 as uuid } from 'uuid';
import findIndex from 'lodash/findIndex';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';

import { validNumeric } from '@jerry-serverless/tools/lib/validators';
import { formatName } from '@jerry-serverless/tools/lib/formatters';
import { VehicleUsageTypes } from '@jerry-serverless/enums/car';

import type { Car, CarUsageEditKey, InitialCar, InitialCarWithID } from '../types/car';
import { CarUsage, CAR_USAGE_MILEAGE, OwnershipEnum } from '../types/car';
import type { AppData, MaintenanceReminderDataType } from '../types/app_data';
import type { SearchPolicyCar } from '../types/policy';
import type { CarInputType } from '../types/car';

export const PARTIAL_VIN_SUFFIX = '1111111';

// Ezlynx returns six 1s for cars prior to 1981.
export const PARTIAL_VIN_OLD_SUFFIX = '111111';

const MIN_MILEAGE = 0;
const MAX_MILEAGE = 500000;

/**
 * Since users aren't seeing a daily mileage input for pulled cars, they might not check the value.
 * We'll default to a lower value and have the users confirm in the FPR flow
 */
export const DEFAULT_PULLED_CARS_ANNUAL_MILEAGE = 25 * 365;
export const AVERAGE_DAILY_MILEAGE = 35;
export const DEFAULT_ANNUAL_MILEAGE = AVERAGE_DAILY_MILEAGE * 365;
export const WARNING_MILEAGE = 20000;
export const ORIGINAL_MILEAGE_INPUT_STORAGE_KEY = 'carOriginalMileages';
export const ORIGINAL_USAGE_INPUT_STORAGE_KEY = 'carOriginalUsages';
export const ORIGINAL_WFH_SELETION_INPUT_STORAGE_KEY = 'carWFHSelection';
export const DAILY_MILEAGE_OPTIONS: number[] = [10, 15, 20, 25, 30, 35, 40, 45, 50, 75];

/**
 * Check if provided annual mileage is valid.
 *
 * @param miles
 * @param minMileage
 * @param maxMileage
 */
export const isValidAnnualMileage = (
  miles?: string | number | null,
  minMileage = MIN_MILEAGE,
  maxMileage = MAX_MILEAGE
): boolean => {
  if (isNil(miles)) return false;

  let milesNum = 0;

  if (typeof miles === 'number') {
    milesNum = miles;
  } else {
    if (!validNumeric(miles, undefined, true)) return false;
    milesNum = parseInt(miles, 10);
  }

  return milesNum >= minMileage && milesNum <= maxMileage;
};

/**
 * Get display title for a car object.
 *
 * @param car
 */
export const carDisplayTitle = (car: Car): string => {
  return `${car.year} ${formatName(car.make || null)} ${formatName(car.model || null)}`;
};

/**
 * Returns a masked version of the VIN.
 * NOTE: supports both full and partial VIN as input.
 *
 * @param vin
 */
export const maskVIN = (vin: string): string => {
  return `${vin.slice(0, 3)}${repeat('X', vin.length - 3)}`;
};

/**
 * Check if mileage selection block should be selected.
 */
export const isMileageSelected = (
  key: CarUsageEditKey,
  showCustomInput: boolean,
  car: Car | null,
  annualMileage?: number
) => {
  if (key === 'Other' && showCustomInput) {
    return true;
  }

  return car?.usage === key && annualMileage === car?.annualMileage;
};

/**
 * Parsing back 'carUsage' DB value to matching frontend selection.
 */
export const mapCarUsageDBValueToFrontendVal = (
  car: Car
): [CarUsageEditKey | undefined, boolean | undefined | null] => {
  // We need to map back matching frontend value to properly show user's previous selection.
  if (car.usage === CarUsage.Pleasure && car.annualMileage === CAR_USAGE_MILEAGE.Pleasure.annualMileage) {
    return ['Pleasure', car.business];
  }

  if (car.usage && CarUsage[car.usage]) return [CarUsage[car.usage], car.business];

  // https://app.asana.com/0/1202292931304613/1206892033744954
  if (
    VehicleUsageTypes.BusinessUse === VehicleUsageTypes[car.usage as VehicleUsageTypes] ||
    VehicleUsageTypes.FarmBusinessUse === VehicleUsageTypes[car.usage as VehicleUsageTypes] ||
    VehicleUsageTypes.FarmUse === VehicleUsageTypes[car.usage as VehicleUsageTypes]
  ) {
    return [CarUsage.Pleasure, true];
  }

  return [undefined, car.business];
};

/**
 * Maps frontend usage key values to DB accepted value.
 */
export const mapCarUsageToDBValue = (car: Car) => {
  if (car.usage && CarUsage[car.usage]) return CarUsage[car.usage];
  return undefined;
};

/**
 * Merge maintenance reminder data to car data as signup4/updatePolicyInfo api param.
 */
export const getCarAndMaintenanceReminderDataForMutation = ({
  cars,
  maintenanceReminderData,
  MaintenanceReminderSettledCars,
}: {
  cars: SearchPolicyCar[] | undefined;
  maintenanceReminderData: Record<string, MaintenanceReminderDataType> | undefined;
  MaintenanceReminderSettledCars: Set<string> | undefined;
}): CarInputType[] => {
  if (cars?.length) {
    return map(cars, (car) => {
      const isMaintenanceReminderSettled = MaintenanceReminderSettledCars?.has(car.id);
      const mrData = isMaintenanceReminderSettled ? maintenanceReminderData?.[car.id] : undefined;
      return {
        id: car.id,
        year: car.year || '',
        ...omitBy(
          {
            make: car.make || undefined,
            model: car.model || undefined,
            submodel: car.submodel || undefined,
            engine: car.engine || undefined,
            vin: car.partialVin || car.vin || undefined,
            isPartialVin: !isNil(car.isPartialVin) ? car.isPartialVin : !!car.partialVin,
            usage: mapCarUsageToDBValue(car),
            ridesharing: !isNil(car.ridesharing) ? car.ridesharing : undefined,
            usedForDelivery: !isNil(car.usedForDelivery) ? car.usedForDelivery : undefined,
            business: !isNil(car.business) ? car.business : undefined,
            annualMileage: isNil(car.annualMileage) ? undefined : car.annualMileage,
            ownership: !isNil(car.ownership) ? car.ownership : undefined,
            userAdded: car.userAdded,
            licensePlateState: car.licensePlateState || undefined,
            licensePlateNumber: car.licensePlateNumber || undefined,
            odometer: car.odometer || undefined,
            additionalInfo: car.additionalInfo || undefined,
          },
          (attrib) => attrib === undefined
        ),
        maintenanceReminderConfig: mrData
          ? {
              oilType: mrData.oilType?.submitValue,
              oilChangeLastCompletedTime: mrData.oilChangeLastCompletedTime?.submitValue,
              tireRotatedLastCompletedTime: mrData.tireRotatedLastCompletedTime?.submitValue || '',
              brakePadsLastCompletedTime: mrData.brakePadsLastCompletedTime?.submitValue || '',
            }
          : undefined,
      };
    });
  } else {
    return [];
  }
};

// Get initial value of car to be updated for 'addCar' steps.
export const initCarForAddCarSteps = (appData: AppData): InitialCarWithID => {
  // Set default values
  const signupParamInitCar = {
    ...(appData.addingNewCar?.car || appData.signupParams?.initialCar),
    annualMileage: appData.signupParams?.initialCar?.annualMileage || DEFAULT_ANNUAL_MILEAGE,
    usage: appData.signupParams?.initialCar?.usage ?? CarUsage.Pleasure,
    ridesharing: appData.signupParams?.initialCar?.ridesharing ?? false,
    usedForDelivery: appData.signupParams?.initialCar?.usedForDelivery ?? false,
    business: appData.signupParams?.initialCar?.business ?? false,
    ownership: appData.signupParams?.initialCar?.ownership ?? OwnershipEnum.Owned,
  };

  // Note: 'uuid' will be only be used from 'addCarYear' step.
  const id = signupParamInitCar?.id || uuid();

  return {
    ...signupParamInitCar,
    id,
  };
};

// List of car add steps.
export type AddCarSteps = 'addCarYear' | 'addCarMake' | 'addCarModel' | 'addCarSubmodel' | 'addCarMileage';

/**
 * Create car update data for 'addCar' steps.
 * Main purpose here is to
 * 1) 'Reset' values if previous selection does not match,
 * 2) Map 'mileage' values per test flags.
 * @param carAttribute
 * @param car
 * @param stepValue
 * @param appData
 * @returns
 */
export const genAddCarStepsUpdateCar = (carAttribute: string, car: InitialCar, stepValues?: InitialCar): InitialCar => {
  // 'mapCarUsageToDBValue' takes default 'Car' type which requires 'id' to be present.
  // Thus we are setting it separtely with default val.
  const id = car?.id || uuid();

  const updateCar = {
    ...car,
    usage: mapCarUsageToDBValue({ ...car, id }),
  };

  const stepValue = stepValues ? stepValues[carAttribute] : '';
  let carUpdates: InitialCar = car;

  // Check if the new value matches previous selection - reset car values if not, otherwise update only the carAttribute value.
  switch (carAttribute) {
    case 'vin':
      carUpdates = stepValue !== updateCar?.vin ? { id: updateCar.id, ...stepValues } : { ...updateCar, ...stepValues };
      break;
    case 'year':
      carUpdates =
        stepValue !== updateCar?.year ? { id: updateCar.id, year: stepValue } : { ...updateCar, year: stepValue };
      break;
    case 'make':
      carUpdates =
        stepValue !== updateCar?.make
          ? { id: updateCar?.id, year: updateCar?.year, make: stepValue }
          : { ...updateCar, make: stepValue };
      break;
    case 'model':
      carUpdates =
        stepValue !== updateCar?.model
          ? { id: updateCar?.id, year: updateCar?.year, make: updateCar?.make, model: stepValue }
          : { ...updateCar, model: stepValue };
      break;
    case 'submodel':
      //  No need to reset anything here.
      carUpdates = { ...updateCar, ...stepValues };
      break;
    case 'mileage':
      carUpdates = updateCar;
      break;
  }
  return carUpdates;
};

/**
 * Get cars from appData.
 *
 * If `appData.tempCars` exists and has cars, use it.
 * Otherwise if we have called updatePolicyInfo, use user cars.
 * Otherwise, use cars from searchPolicyData.
 */
export const getLatestCars = (appData: AppData) => {
  // If we have sync'd 'tempCars', return it.
  if (appData.tempCars && appData.tempCars.length > 0) {
    return [...appData.tempCars];
  }

  // Check if we are pulling from 'searchPolicyInfo' query result vs saved cars data.
  return appData.syncedPolicyData
    ? filter(appData.cars || [], (car) => !car.isArchived)
    : appData.searchPolicyData?.cars || [];
};

/**
 * Return policy data based on test flag.
 */
export const getInitialCarsData = (appData: AppData) => {
  const policyDataCarsUpdate = cloneDeep(appData.searchPolicyData);
  // Technically, we will always have 'searchPolicyData' since we init it with initial value on loading step.
  if (policyDataCarsUpdate) {
    policyDataCarsUpdate.cars = getLatestCars(appData);

    // If we have initial car data from user, append it to policy data.
    if (appData.signupParams?.initialCar?.partialVin || appData.signupParams?.initialCar?.vin) {
      // We have to cast it to 'SearchPolicyCar' for direct insertion.
      const carUpdate = {
        ...(appData.signupParams?.initialCar as SearchPolicyCar),
        userAdded: true,
        selected: true,
      };

      // Find ID
      const carExistIdx = findIndex(policyDataCarsUpdate.cars, (searchedCar) => searchedCar.id === carUpdate.id);
      // If car was already added, update car.
      if (carExistIdx >= 0) {
        policyDataCarsUpdate.cars[carExistIdx] = carUpdate;
      } else {
        policyDataCarsUpdate.cars = [...policyDataCarsUpdate.cars, carUpdate];
      }
    }
    return policyDataCarsUpdate;
  } else if (appData.signupParams?.needNNOInsurance !== undefined && appData.signupParams.initialCar?.id) {
    return {
      cars: [{ ...appData.signupParams.initialCar, userAdded: true, selected: true } as SearchPolicyCar],
      drivers: sortBy(
        filter(appData.drivers || [], (drv) => !drv.isArchived),
        (drv) => drv.id !== appData.driverID
      ),
      coverages: [],
      isImported: false,
      isPredicted: appData.signupParams?.isPredicted || false,
    };
    // SkipLN, skipped the searchPolicyData
  } else if (appData.signupParams?.multipleCarsAndDrivers === false) {
    return {
      cars: filter(appData.cars || [], (car) => !car.isArchived),
      drivers: sortBy(
        filter(appData.drivers || [], (drv) => !drv.isArchived),
        (drv) => drv.id !== appData.driverID
      ),
      coverages: [],
      isImported: false,
      isPredicted: appData.signupParams?.isPredicted || false,
    };
  }

  return appData.searchPolicyData;
};

export const isValidDailyMileage = (dailyMileage: number): boolean => includes(DAILY_MILEAGE_OPTIONS, dailyMileage);

export const getNearestDailyMileage = (annualMileage: number): number => {
  const dailyMileage = Math.round(annualMileage / 365);
  return DAILY_MILEAGE_OPTIONS.reduce((prev, curr) =>
    Math.abs(curr - dailyMileage) < Math.abs(prev - dailyMileage) ? curr : prev
  );
};

export const getFullCoverageCarCandidates = (appData: AppData) => {
  const nonFinancedCars = appData.selectedCars?.filter(
    (car) => car.ownership !== OwnershipEnum.Leased && car.ownership !== OwnershipEnum.Lien
  );
  if (nonFinancedCars && nonFinancedCars.length > 0) {
    return nonFinancedCars.filter((car) => {
      const carValue = appData.carValues?.[car.id];
      return carValue && carValue > 12000;
    });
  }
  return [];
};
